import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;
const SOCKET_URL = window.location.origin;

function DexPage() {
  const navigate = useNavigate();
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const launchesRef = useRef(new Map());
  
  // Sort State
  const [sortBy, setSortBy] = useState('age'); // 'age' or 'marketcap'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Initial Load
  useEffect(() => {
    loadDexLaunches();
    setupSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Socket Setup
  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [DEX SOCKET] ✅ Connected to server`);
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [DEX SOCKET] ❌ Disconnected from server`);
        setIsConnected(false);
      });

      // DEX Status Updates
      socket.on('dex-status-update', (update) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [DEX SOCKET] 📡 DEX Status Update: ${update.tokenmint?.substring(0, 8)}... | Status: ${update.dex_status}`);
        handleDexStatusUpdate(update);
      });

      // Market Cap Updates (falls sich MC ändert)
      socket.on('market-cap-update', (update) => {
        handleMarketCapUpdate(update);
      });

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('dex-status-update');
        socket.off('market-cap-update');
      };
    }
  }, [socket]);

  const setupSocket = () => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    setSocket(newSocket);
  };

  const loadDexLaunches = async (retryCount = 0, preserveExisting = false) => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE}/api/dex`, {
        timeout: 10000,
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      });
      
      const launchesData = response.data.data || [];
      
      if (Array.isArray(launchesData)) {
        // Wenn preserveExisting true ist, merge mit bestehenden Launches
        if (preserveExisting) {
          setLaunches(prevLaunches => {
            const existingMap = new Map(prevLaunches.map(l => [l.tokenmint, l]));
            const newMap = new Map(launchesData.map(l => [l.tokenmint, l]));
            
            // Merge: Behalte bestehende Coins, aktualisiere mit neuen Daten
            const merged = launchesData.map(newLaunch => {
              const existing = existingMap.get(newLaunch.tokenmint);
              if (existing) {
                // Merge: Behalte alle Felder, aktualisiere mit neuen Daten
                return { ...existing, ...newLaunch };
              }
              return newLaunch;
            });
            
            // WICHTIG: Füge ALLE Coins hinzu, die in prevLaunches sind aber nicht in launchesData
            // Das verhindert, dass Coins verschwinden, wenn sie aus dem 8-Stunden-Fenster fallen
            prevLaunches.forEach(existingLaunch => {
              if (!newMap.has(existingLaunch.tokenmint)) {
                // Prüfe ob Coin noch DEX Status hat (sollte behalten werden)
                if (existingLaunch.dex_status === 'PENDING' || 
                    existingLaunch.dex_status === 'APPROVED' || 
                    existingLaunch.dex_status === 'PAID') {
                  merged.push(existingLaunch);
                  console.log(`[DEX MERGE] Keeping existing coin ${existingLaunch.tokenmint.substring(0, 8)}... with status ${existingLaunch.dex_status}`);
                }
              }
            });
            
            // Sortiere nach DEX Payment Timestamp (neueste zuerst)
            merged.sort((a, b) => {
              const timestampA = a.dex_payment_timestamp || a.dex_check_timestamp || 0;
              const timestampB = b.dex_payment_timestamp || b.dex_check_timestamp || 0;
              return timestampB - timestampA; // Absteigend (neueste zuerst)
            });
            
            // Update Ref
            launchesRef.current.clear();
            merged.forEach(launch => {
              launchesRef.current.set(launch.tokenmint, launch);
            });
            
            console.log(`[DEX MERGE] Merged ${merged.length} coins (${prevLaunches.length} existing, ${launchesData.length} new)`);
            
            return merged;
          });
        } else {
          // Sortiere nach DEX Payment Timestamp (neueste zuerst)
          const sorted = [...launchesData].sort((a, b) => {
            const timestampA = a.dex_payment_timestamp || a.dex_check_timestamp || 0;
            const timestampB = b.dex_payment_timestamp || b.dex_check_timestamp || 0;
            return timestampB - timestampA; // Absteigend (neueste zuerst)
          });
          
          setLaunches(sorted);
          
          // Update Ref
          launchesRef.current.clear();
          sorted.forEach(launch => {
            launchesRef.current.set(launch.tokenmint, launch);
          });
        }
        
        setLoading(false);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      console.error('[DEX API] Error:', err);
      
      // Retry logic
      if ((err.code === 'ERR_NETWORK' || err.message.includes('CONTENT_LENGTH_MISMATCH') || err.code === 'ERR_CONTENT_LENGTH_MISMATCH') && retryCount < 3) {
        console.log(`[DEX API] Retrying... (${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return loadDexLaunches(retryCount + 1, preserveExisting);
      }
      
      // WICHTIG: Bei Fehler NICHT die Liste leeren, wenn preserveExisting true ist
      if (!preserveExisting) {
        setError(err.message);
        setLoading(false);
      } else {
        // Bei preserveExisting nur Error loggen, aber Coins behalten
        console.error('[DEX API] Error but preserving existing coins:', err.message);
      }
    }
  };

  const handleDexStatusUpdate = (update) => {
    console.log(`[DEX UPDATE] Received update for ${update.tokenmint?.substring(0, 8)}... | Status: ${update.dex_status}`);
    
    setLaunches(prevLaunches => {
      console.log(`[DEX UPDATE] Current launches count: ${prevLaunches.length}`);
      
      const found = prevLaunches.find(l => l.tokenmint === update.tokenmint);
      if (!found) {
        // Neuer Coin mit DEX Status - lade vollständige Daten von der API
        if (update.dex_status === 'PENDING' || update.dex_status === 'APPROVED' || update.dex_status === 'PAID') {
          console.log(`[DEX UPDATE] New coin with DEX status, reloading...`);
          // Lade die vollständigen Coin-Daten von der API (preserveExisting = true, damit bestehende Coins nicht verschwinden)
          loadDexLaunches(0, true).catch(err => {
            console.error('[DEX] Error reloading after new DEX status:', err);
          });
        }
        return prevLaunches;
      }
      
      // Update bestehenden Coin
      const updatedLaunches = prevLaunches.map(launch => {
        if (launch.tokenmint === update.tokenmint) {
          const updated = {
            ...launch,
            dex_status: update.dex_status,
            dex_payment_timestamp: update.dex_payment_timestamp,
            dex_check_timestamp: update.dex_check_timestamp,
            dex_payment_time_ago: update.dex_payment_time_ago
          };
          
          // Update Ref
          launchesRef.current.set(update.tokenmint, updated);
          
          console.log(`[DEX UPDATE] Updated coin ${update.tokenmint.substring(0, 8)}... | New status: ${update.dex_status}`);
          
          return updated;
        }
        return launch;
      });
      
      // Sortiere nach DEX Payment Timestamp (neueste zuerst)
      updatedLaunches.sort((a, b) => {
        const timestampA = a.dex_payment_timestamp || a.dex_check_timestamp || 0;
        const timestampB = b.dex_payment_timestamp || b.dex_check_timestamp || 0;
        return timestampB - timestampA; // Absteigend (neueste zuerst)
      });
      
      console.log(`[DEX UPDATE] After update: ${updatedLaunches.length} coins`);
      
      return updatedLaunches;
    });
  };

  const handleMarketCapUpdate = (update) => {
    setLaunches(prevLaunches => {
      const found = prevLaunches.find(l => l.tokenmint === update.tokenmint);
      if (!found) return prevLaunches;
      
      return prevLaunches.map(launch => {
        if (launch.tokenmint === update.tokenmint) {
          const updated = {
            ...launch,
            currentmarketcapsol: update.currentmarketcapsol,
            currentmarketcapsol_usd: update.currentmarketcapsol_usd,
            peakmarketcapsol: update.peakmarketcapsol,
            peakmarketcapsol_usd: update.peakmarketcapsol_usd,
            _updateAnimation: true
          };
          
          launchesRef.current.set(update.tokenmint, updated);
          
          setTimeout(() => {
            setLaunches(prev => prev.map(l => {
              if (l.tokenmint === update.tokenmint && l._updateAnimation) {
                const { _updateAnimation, ...rest } = l;
                return rest;
              }
              return l;
            }));
          }, 500);
          
          return updated;
        }
        return launch;
      });
    });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = Date.now();
    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const diff = now - time;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        return `${days} day${days > 1 ? 's' : ''} ${remainingHours} hour${remainingHours > 1 ? 's' : ''} ago`;
      }
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min ago`;
      }
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} min ago`;
    } else {
      return `${seconds} sec ago`;
    }
  };

  const formatNumber = (num) => {
    const value = parseFloat(num);
    if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortLaunches = (launches) => {
    return [...launches].sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'age') {
        aValue = a.created_at_timestamp || new Date(a.created_at).getTime();
        bValue = b.created_at_timestamp || new Date(b.created_at).getTime();
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      } else if (sortBy === 'marketcap') {
        aValue = parseFloat(a.currentmarketcapsol_usd || 0);
        bValue = parseFloat(b.currentmarketcapsol_usd || 0);
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      }
      
      return 0;
    });
  };

  const sortedLaunches = sortLaunches(launches);

  return (
    <div className="App">
      <Header />

      <main className="app-main">
        <div className="page-header">
          <h2 className="page-title">DEX Payment Status</h2>
          <p className="page-subtitle">
            Many devs launch and dump. When they pay for DEX, they often mean business with the coin.
            We check all coins for DEX paid status and show them here in real-time.
          </p>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading DEX data...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>Error: {error}</p>
            <button className="retry-btn" onClick={() => loadDexLaunches()}>
              Retry
            </button>
          </div>
        ) : launches.length === 0 ? (
          <div className="empty-container">
            <p>No coins with DEX status found</p>
          </div>
        ) : (
          <>
            <div className="single-column-layout">
              <div className="column-header">
                <h2>DEX PAYMENT STATUS</h2>
                <div className="sort-buttons">
                  <button 
                    className={`sort-btn ${sortBy === 'age' ? 'active' : ''}`}
                    onClick={() => toggleSort('age')}
                  >
                    AGE {sortBy === 'age' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button 
                    className={`sort-btn ${sortBy === 'marketcap' ? 'active' : ''}`}
                    onClick={() => toggleSort('marketcap')}
                  >
                    MARKETCAP {sortBy === 'marketcap' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </div>
              </div>
              
              <div className="launches-list">
                {sortedLaunches.map((launch) => (
                  <div key={launch.tokenmint} className="launch-row">
                    {/* Desktop Layout */}
                    <div className="desktop-layout">
                      <div className="row-time">
                        <div className="row-time-left">
                          {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                          {launch.imageurl && (
                            <img 
                              src={launch.imageurl} 
                              alt={launch.symbol || launch.name || 'Coin'}
                              className="coin-image-small"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      </div>
                      
                      <div className="row-symbol">
                        <div className="symbol-name">{launch.symbol || launch.name || 'N/A'}</div>
                        <div 
                          className="row-ca clickable"
                          onClick={() => copyToClipboard(launch.tokenmint)}
                          title="Click to copy"
                        >
                          {launch.tokenmint.substring(0, 8)}...{launch.tokenmint.substring(launch.tokenmint.length - 4)}
                        </div>
                      </div>
                      
                      <div className="row-dex">
                        {(() => {
                          const dexStatus = launch.dex_status;
                          if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                            return (
                              <div className={`dex-badge-small ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                <div>DEX PAID</div>
                                {launch.dex_payment_time_ago && (
                                  <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="dex-badge-small dex-not-paid">
                              DEX NOT PAID
                            </div>
                          );
                        })()}
                      </div>
                      
                      <div className="row-dev">
                        <div className="dev-label">DEV</div>
                        <div 
                          className="dev-ca clickable"
                          onClick={() => {
                            copyToClipboard(launch.creator);
                            navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                          }}
                          title="Click to copy and view dev details"
                        >
                          {launch.creator.substring(0, 8)}...{launch.creator.substring(launch.creator.length - 4)}
                        </div>
                        <div className="bonding-rate-small">{parseFloat(launch.bonding_rate_percent || 0).toFixed(1)}%</div>
                      </div>
                      
                      <div className="row-actions">
                        <a 
                          href={`https://pump.fun/coin/${launch.tokenmint}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn-small pump-btn"
                        >
                          🚀 Pump.fun
                        </a>
                        <a 
                          href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn-small bullx-btn"
                        >
                          📊 BullX
                        </a>
                      </div>
                      
                      <div className="row-mc">
                        <div className="mc-label-small">CURRENT MC</div>
                        <div className={`mc-value-large green ${launch._updateAnimation ? 'updating' : ''}`}>
                          ${formatNumber(launch.currentmarketcapsol_usd || 0)}
                        </div>
                        <div className="ath-label-small">ATH</div>
                        <div className="ath-value-medium">${formatNumber(launch.peakmarketcapsol_usd || 0)}</div>
                      </div>
                    </div>
                    
                    {/* Mobile Layout */}
                    <div className="mobile-layout">
                      {/* Ganz oben: Zeit */}
                      <div className="mobile-time-top">
                        {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                      </div>
                      
                      {/* Bild */}
                      {launch.imageurl && (
                        <div className="mobile-image-container">
                          <img 
                            src={launch.imageurl} 
                            alt={launch.symbol || launch.name || 'Coin'}
                            className="coin-image-mobile"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Symbol */}
                      <div className="mobile-symbol">
                        {launch.symbol || launch.name || 'N/A'}
                      </div>
                      
                      {/* Tokenmint - größer */}
                      <div 
                        className="mobile-tokenmint clickable"
                        onClick={() => copyToClipboard(launch.tokenmint)}
                        title="Click to copy"
                      >
                        {launch.tokenmint}
                      </div>
                      
                      {/* MC und ATH nebeneinander, je 50% */}
                      <div className="mobile-mc-ath-row">
                        <div className="mobile-mc-box">
                          <div className="mobile-mc-label">Current MC</div>
                          <div className={`mobile-mc-value ${launch._updateAnimation ? 'updating' : ''}`}>
                            ${formatNumber(launch.currentmarketcapsol_usd || 0)}
                          </div>
                        </div>
                        <div className="mobile-ath-box">
                          <div className="mobile-ath-label">ATH</div>
                          <div className="mobile-ath-value">${formatNumber(launch.peakmarketcapsol_usd || 0)}</div>
                        </div>
                      </div>
                      
                      {/* DEV und DEX nebeneinander, je 50% */}
                      <div className="mobile-dev-dex-row">
                        <div className="mobile-dev-box">
                          <div className="mobile-dev-label">DEV</div>
                          <div 
                            className="mobile-dev-ca clickable"
                            onClick={() => {
                              copyToClipboard(launch.creator);
                              navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                            }}
                            title="Click to copy and view dev details"
                          >
                            {launch.creator}
                          </div>
                          <div className="mobile-bonding-rate">{parseFloat(launch.bonding_rate_percent || 0).toFixed(1)}%</div>
                        </div>
                        <div className="mobile-dex-box">
                          {(() => {
                            const dexStatus = launch.dex_status;
                            if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                              return (
                                <div className={`mobile-dex-badge ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                  <div>DEX PAID</div>
                                  {launch.dex_payment_time_ago && (
                                    <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="mobile-dex-badge dex-not-paid">
                                DEX NOT PAID
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      
                      {/* Buttons nebeneinander */}
                      <div className="mobile-actions-row">
                        <a 
                          href={`https://pump.fun/coin/${launch.tokenmint}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mobile-action-btn pump-btn"
                        >
                          🚀 Pump.fun
                        </a>
                        <a 
                          href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mobile-action-btn bullx-btn"
                        >
                          📊 BullX
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default DexPage;

